import {Banner, Button, Dialog, TextField, toast} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';

import {useUpdateRun} from '../../state/runs/hooks';
import {setCustomRunName as setCustomRunNameAction} from '../../state/views/customRunNames/actions';
import {type Ref as CustomRunNamesRef} from '../../state/views/customRunNames/types';
import {useMaybeViewAction, usePartMaybe} from '../../state/views/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {getCustomRunName} from '../../util/runs';
import {useIsInLoadedReportContext} from '../ReportPage/LoadedReportContext';

type RenameRunDialogProps = {
  runId: string;
  runName: string;
  runDisplayName: string;
  customRunNamesRef: CustomRunNamesRef | undefined;
  closeDialog: () => void;
  isCrossProjectRun: boolean;
  isOpen: boolean;
};

export const RenameRunDialog = ({
  runId,
  runName,
  runDisplayName,
  customRunNamesRef,
  closeDialog,
  isCrossProjectRun,
  isOpen,
}: RenameRunDialogProps) => {
  const isInReport = useIsInLoadedReportContext();

  const updateRun = useUpdateRun({
    context: propagateErrorsContext(),
  });
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunName = getCustomRunName(runName, false, customRunNames) ?? '';
  const setCustomRunName = useMaybeViewAction(
    customRunNamesRef,
    setCustomRunNameAction
  );

  const [newDisplayName, setNewDisplayName] = useState(runDisplayName);
  const [newCustomName, setNewCustomName] = useState(customRunName);

  const onSave = async () => {
    if (newDisplayName !== runDisplayName) {
      try {
        await updateRun(runId, runName, {displayName: newDisplayName});
      } catch (err) {
        toast.error('Failed to update run name');
      }
    }
    if (newCustomName !== customRunName) {
      setCustomRunName(runName, newCustomName);
    }
    closeDialog();
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSave();
    }
  };

  return (
    <Dialog.Modal
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          closeDialog();
        }
      }}>
      <Dialog.Heading>Edit run name</Dialog.Heading>
      <Dialog.Content>
        <TailwindContents>
          <div className="text-wb-text-secondary dark:text-moon-300">
            Edit the run name for the entire project, or add a display name for
            this {isInReport ? 'panel grid' : 'workspace'} only.
            {isCrossProjectRun && (
              <Banner variant="warning" mt={6} isDismissible={false}>
                <span>
                  <b>This run belongs to another project.</b> Renaming it will
                  affect the source project and anywhere else it is used.
                </span>
              </Banner>
            )}
            <div className="mt-16 flex flex-col gap-12">
              <TextField
                label="Run name"
                value={newDisplayName}
                onChange={setNewDisplayName}
                onKeyDown={onEnterKeyDown}
                placeholder="Choose a run name"
                fullWidth
                autoFocus_I_READ_THE_JSDOC
              />
              <TextField
                label={`Display name (this ${
                  isInReport ? 'panel grid' : 'workspace'
                })`}
                value={newCustomName}
                onChange={setNewCustomName}
                onKeyDown={onEnterKeyDown}
                placeholder={newDisplayName || 'Choose a display name'}
                fullWidth
              />
            </div>
          </div>
        </TailwindContents>
      </Dialog.Content>
      <Dialog.Footer>
        <Button
          isDisabled={
            !newDisplayName ||
            (newDisplayName === runDisplayName &&
              newCustomName === customRunName)
          }
          onPress={onSave}>
          Save
        </Button>
        <Button variant="secondary" onPress={closeDialog}>
          Cancel
        </Button>
      </Dialog.Footer>
    </Dialog.Modal>
  );
};
